import { useCallback, useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { requireSupabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
  blankConn,
  blankPipe,
  type GhlAvailPipeline,
  type GhlConnection,
  type GhlNewConnectionDraft,
  type GhlNewPipelineDraft,
  type GhlPipeline,
  type LocationOption,
  type SyncResult,
} from "@/components/admin/ghl/ghlSectionShared";
import { fetchGhlSectionData } from "@/components/admin/ghl/ghlSectionLoad";
import { applyPipelineNameAutofill } from "@/components/admin/ghl/ghlSectionAutofillFromName";
import {
  ghlDbInsertConnection,
  ghlDbInsertPipeline,
  ghlDbSetConnectionActive,
  ghlDbSetPipelineActive,
  ghlDbUpdateApiKey,
  ghlDbUpdateWebhook,
} from "@/components/admin/ghl/ghlSectionWriteDb";

export function useGhlSection(organizationId: string, orgName?: string) {
  const auth = useAuth();
  const sb = requireSupabase() as SupabaseClient;

  const canTogglePipelineActive = auth.hasRole("admin", "owner", "dev", "platform_admin", "platform_dev");

  const [connections, setConnections] = useState<GhlConnection[]>([]);
  const [pipelinesByConn, setPipelinesByConn] = useState<Record<string, GhlPipeline[]>>({});
  const [showHelp, setShowHelp] = useState(false);
  const [locations, setLocations] = useState<LocationOption[]>([]);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [knownProductTypes, setKnownProductTypes] = useState<string[]>([]);
  const [availPipelines, setAvailPipelines] = useState<Record<string, GhlAvailPipeline[]>>({});
  const [loadingPipes, setLoadingPipes] = useState<Record<string, boolean>>({});
  const [autofillMsg, setAutofillMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [collapsedConns, setCollapsedConns] = useState<Set<string>>(new Set());
  const [editKeyFor, setEditKeyFor] = useState<string | null>(null);
  const [editKeyValue, setEditKeyValue] = useState("");
  const [editWebhookFor, setEditWebhookFor] = useState<string | null>(null);
  const [editWebhookValue, setEditWebhookValue] = useState("");
  const [showAddConn, setShowAddConn] = useState(false);
  const [showAddPipeFor, setShowAddPipeFor] = useState<string | null>(null);
  const [newConn, setNewConn] = useState<GhlNewConnectionDraft>(blankConn);
  const [newPipe, setNewPipe] = useState<GhlNewPipelineDraft>(blankPipe);

  const toggleCollapse = (connId: string) =>
    setCollapsedConns((prev) => {
      const next = new Set(prev);
      if (next.has(connId)) next.delete(connId);
      else next.add(connId);
      return next;
    });

  const load = useCallback(async () => {
    setLoading(true);
    setMsg("");
    try {
      const snap = await fetchGhlSectionData(sb, organizationId);
      setConnections(snap.connections);
      setLocations(snap.locations);
      setKnownProductTypes(snap.knownProductTypes);
      setPipelinesByConn(snap.pipelinesByConn);
    } catch (e: unknown) {
      setMsg((e as Error)?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, [organizationId, sb]);

  useEffect(() => {
    void load();
  }, [load]);

  const openAddPipeline = async (connId: string) => {
    setShowAddPipeFor(connId);
    setNewPipe(blankPipe);
    setAutofillMsg(null);
    if (availPipelines[connId]) return;
    setLoadingPipes((lp) => ({ ...lp, [connId]: true }));
    try {
      const token = auth.session?.access_token;
      if (!token) throw new Error("Not signed in.");
      const res = await fetch(`/api/ghl/pipelines?connectionId=${connId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = (await res.json()) as { pipelines?: GhlAvailPipeline[]; error?: string };
      if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
      const alreadyAdded = new Set((pipelinesByConn[connId] ?? []).map((p) => p.ghl_pipeline_id));
      const filtered = (json.pipelines ?? []).filter((p) => !alreadyAdded.has(p.id));
      setAvailPipelines((ap) => ({ ...ap, [connId]: filtered }));
    } catch (e: unknown) {
      setMsg((e as Error)?.message ?? String(e));
    } finally {
      setLoadingPipes((lp) => ({ ...lp, [connId]: false }));
    }
  };

  const tryAutofillFromName = (pipelineName: string) =>
    applyPipelineNameAutofill(pipelineName, locations, setNewPipe, setAutofillMsg);

  const triggerSync = async () => {
    const token = auth.session?.access_token;
    if (!token) {
      setMsg("Not signed in.");
      return;
    }
    setSyncing(true);
    setSyncResult(null);
    setMsg("");
    try {
      const res = await fetch("/api/ghl/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ organizationId }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`);
      setSyncResult(json as SyncResult);
      await load();
    } catch (e: unknown) {
      setMsg((e as Error)?.message ?? String(e));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (showAddConn && orgName && !newConn.company_name) {
      setNewConn((c) => ({ ...c, company_name: orgName }));
    }
  }, [showAddConn, orgName, newConn.company_name]);

  const updateApiKey = async (connId: string) => {
    if (!editKeyValue.trim()) return;
    setLoading(true);
    setMsg("");
    try {
      await ghlDbUpdateApiKey(sb, connId, editKeyValue.trim());
      setEditKeyFor(null);
      setEditKeyValue("");
      setMsg("API key updated.");
    } catch (e: unknown) {
      setMsg((e as Error)?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  };

  const updateWebhookUrl = async (connId: string) => {
    setLoading(true);
    setMsg("");
    try {
      await ghlDbUpdateWebhook(sb, connId, editWebhookValue.trim() || null);
      setEditWebhookFor(null);
      setEditWebhookValue("");
      setMsg("Webhook URL updated.");
      await load();
    } catch (e: unknown) {
      setMsg((e as Error)?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  };

  const addConnection = async () => {
    if (!newConn.company_name.trim() || !newConn.ghl_location_id.trim() || !newConn.ghl_api_key.trim()) {
      setMsg("Company name, GHL location ID, and API key are all required.");
      return;
    }
    setLoading(true);
    setMsg("");
    try {
      await ghlDbInsertConnection(sb, organizationId, newConn);
      setNewConn(blankConn);
      setShowAddConn(false);
      await load();
    } catch (e: unknown) {
      setMsg((e as Error)?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  };

  const addPipeline = async (connectionId: string) => {
    if (!newPipe.ghl_pipeline_id.trim() || !newPipe.pipeline_name.trim()) {
      setMsg("GHL pipeline ID and pipeline name are required.");
      return;
    }
    setLoading(true);
    setMsg("");
    try {
      await ghlDbInsertPipeline(sb, connectionId, newPipe);
      setNewPipe(blankPipe);
      setShowAddPipeFor(null);
      await load();
    } catch (e: unknown) {
      setMsg((e as Error)?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  };

  const togglePipeline = async (pipelineId: string, current: boolean, pipelineName?: string) => {
    if (current) {
      const label = pipelineName ? `"${pipelineName}"` : "this pipeline";
      if (!window.confirm(`Deactivate ${label}? It will stop being included in syncs.`)) return;
    }
    setLoading(true);
    setMsg("");
    try {
      await ghlDbSetPipelineActive(sb, pipelineId, !current);
      await load();
    } catch (e: unknown) {
      setMsg((e as Error)?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  };

  const toggleConnection = async (connId: string, current: boolean, companyName?: string) => {
    if (current) {
      const label = companyName ? `"${companyName}"` : "this connection";
      if (!window.confirm(`Deactivate ${label}? All pipelines under it will stop syncing.`)) return;
    }
    setLoading(true);
    setMsg("");
    try {
      await ghlDbSetConnectionActive(sb, connId, !current);
      await load();
    } catch (e: unknown) {
      setMsg((e as Error)?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  };

  const locationName = (id: string | null) => (id ? (locations.find((l) => l.id === id)?.name ?? id) : null);

  return {
    canTogglePipelineActive,
    connections,
    pipelinesByConn,
    showHelp,
    setShowHelp,
    collapsedConns,
    toggleCollapse,
    loading,
    msg,
    syncResult,
    syncing,
    knownProductTypes,
    availPipelines,
    loadingPipes,
    autofillMsg,
    editKeyFor,
    editKeyValue,
    setEditKeyValue,
    editWebhookFor,
    editWebhookValue,
    setEditWebhookValue,
    setEditKeyFor,
    setEditWebhookFor,
    showAddConn,
    setShowAddConn,
    showAddPipeFor,
    setShowAddPipeFor,
    newConn,
    setNewConn,
    newPipe,
    setNewPipe,
    setAutofillMsg,
    triggerSync,
    openAddPipeline,
    tryAutofillFromName,
    updateApiKey,
    updateWebhookUrl,
    addConnection,
    addPipeline,
    togglePipeline,
    toggleConnection,
    locationName,
    locations,
  };
}
